import { type FunctionComponent } from 'react'
import { Menu } from '@mantine/core'
import { type IconComponentType } from '@instacart/ids-core'
import { typography, useTheme } from '@retailer-platform/shared-components/src/tds'

// IPP-local override of TDS's `MenuItem` that adds a `trailingIcon` slot.
// TDS's MenuItem only exposes a `leftIcon`; we need a right-aligned icon to
// signal "opens in a new window" on AppSwitcher entries that link out.
// Mirrors TDS's MenuItem layout/styling so the two render identically apart
// from the trailing slot.

export interface TdsMenuItemProps {
  compact?: boolean
  title?: string
  text?: string
  disabled?: boolean
  leftIcon?: IconComponentType
  trailingIcon?: IconComponentType
  onClick?: () => void
  dataTestId?: string
  href?: string
  target?: string
  rel?: string
}

export const TdsMenuItem: FunctionComponent<TdsMenuItemProps> = ({
  compact,
  title,
  text,
  disabled,
  leftIcon: LeftIcon,
  trailingIcon: TrailingIcon,
  onClick,
  dataTestId,
  href,
  target,
  rel,
}) => {
  const theme = useTheme()
  const headerTypography = compact ? typography.accentEmphasized : typography.bodyEmphasized
  const textTypography = compact ? typography.accentRegular : typography.bodyRegular
  const hoveredBackground = theme.colors.brandPrimaryLight
  const iconColor = disabled ? theme.colors.systemGrayscale30 : theme.colors.systemGrayscale80
  const trailingIconColor = disabled
    ? theme.colors.systemGrayscale30
    : theme.colors.brandPrimaryDark

  const bodyCss = {
    boxSizing: 'border-box' as const,
    backgroundColor: theme.colors.systemGrayscale00,
    color: disabled ? theme.colors.systemGrayscale30 : theme.colors.systemGrayscale80,
    padding: compact ? '6px 16px' : '10px 16px',
    cursor: disabled ? ('not-allowed' as const) : ('pointer' as const),
    border: 0,
    pointerEvents: 'auto' as const,
    minWidth: 300,
    '&:hover': { backgroundColor: hoveredBackground },
    ':focus-visible': {
      backgroundColor: theme.colors.brandPrimaryLight,
      outline: `2px solid ${theme.colors.brandPrimaryDark}`,
      outlineOffset: '2px',
      zIndex: 1,
    },
  }

  const body = (
    <div css={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
      {LeftIcon && (
        <div css={{ flexShrink: 0 }}>
          <LeftIcon
            css={{ position: 'relative', top: 2 }}
            size={16}
            fill={iconColor}
            data-testid="menu-item-left-icon"
          />
        </div>
      )}
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexGrow: 1,
        }}
      >
        <div css={{ ...headerTypography, wordBreak: 'break-word', textAlign: 'left' }}>{title}</div>
        <div css={{ ...textTypography, wordBreak: 'break-word', textAlign: 'left' }}>{text}</div>
      </div>
      {TrailingIcon && (
        <div css={{ flexShrink: 0, alignSelf: 'center' }}>
          <TrailingIcon size={16} fill={trailingIconColor} data-testid="menu-item-trailing-icon" />
        </div>
      )}
    </div>
  )

  if (href !== undefined) {
    return (
      <Menu.Item
        component="a"
        href={href}
        target={target}
        rel={rel}
        css={bodyCss}
        disabled={disabled}
        data-testid={dataTestId}
      >
        {body}
      </Menu.Item>
    )
  }

  return (
    <Menu.Item css={bodyCss} disabled={disabled} onClick={onClick} data-testid={dataTestId}>
      {body}
    </Menu.Item>
  )
}
